import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as deliveryNoteService from "../../../features/deliverynotes/services/deliveryNoteService";

// Async thunks
export const fetchDeliveryNotes = createAsyncThunk(
  "deliveryNote/fetchDeliveryNotes",
  async (_, { rejectWithValue }) => {
    try {
      const deliveryNotes = await deliveryNoteService.getDeliveryNotes();
      return deliveryNotes;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addDeliveryNoteToStore = createAsyncThunk(
  "deliveryNote/addDeliveryNote",
  async (deliveryNoteData, { rejectWithValue }) => {
    try {
      const newDeliveryNote = await deliveryNoteService.addDeliveryNote(deliveryNoteData);
      return newDeliveryNote;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateDeliveryNoteInStore = createAsyncThunk(
  "deliveryNote/updateDeliveryNote",
  async ({ id, deliveryNoteData }, { rejectWithValue }) => {
    try {
      const updatedDeliveryNote = await deliveryNoteService.updateDeliveryNote(id, deliveryNoteData);
      return updatedDeliveryNote;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteDeliveryNoteFromStore = createAsyncThunk(
  "deliveryNote/deleteDeliveryNote",
  async (id, { rejectWithValue }) => {
    try {
      await deliveryNoteService.deleteDeliveryNote(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const deliveryNoteSlice = createSlice({
  name: "deliveryNote",
  initialState: {
    deliveryNotes: [],
    selectedDeliveryNote: null,
    selectedCustomer: null,
    selectedProduct: null,
    quantity: 1,
    price: "",
    customText: "",
    loading: false,
    error: null,
  },
  reducers: {
    setSelectedDeliveryNote: (state, action) => {
      state.selectedDeliveryNote = action.payload;
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    setQuantity: (state, action) => {
      state.quantity = action.payload;
    },
    setPrice: (state, action) => {
      state.price = action.payload;
    },
    setCustomText: (state, action) => {
      state.customText = action.payload;
    },
    clearDeliveryNoteState: (state) => {
      state.selectedDeliveryNote = null;
      state.selectedCustomer = null;
      state.selectedProduct = null;
      state.quantity = 1;
      state.price = "";
      state.customText = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch delivery notes
      .addCase(fetchDeliveryNotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryNotes.fulfilled, (state, action) => {
        state.loading = false;
        state.deliveryNotes = action.payload;
      })
      .addCase(fetchDeliveryNotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add delivery note
      .addCase(addDeliveryNoteToStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDeliveryNoteToStore.fulfilled, (state, action) => {
        state.loading = false;
        state.deliveryNotes.push(action.payload);
      })
      .addCase(addDeliveryNoteToStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update delivery note
      .addCase(updateDeliveryNoteInStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDeliveryNoteInStore.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.deliveryNotes.findIndex((dn) => dn.Id === action.payload.Id);
        if (index !== -1) {
          state.deliveryNotes[index] = action.payload;
        }
      })
      .addCase(updateDeliveryNoteInStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete delivery note
      .addCase(deleteDeliveryNoteFromStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDeliveryNoteFromStore.fulfilled, (state, action) => {
        state.loading = false;
        state.deliveryNotes = state.deliveryNotes.filter((dn) => dn.Id !== action.payload);
      })
      .addCase(deleteDeliveryNoteFromStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedDeliveryNote,
  setSelectedCustomer,
  setSelectedProduct,
  setQuantity,
  setPrice,
  setCustomText,
  clearDeliveryNoteState,
} = deliveryNoteSlice.actions;

export default deliveryNoteSlice.reducer;
